import { Edge } from 'reactflow';
import { colourPurple06, colourRed06, colourRed09, colourYellow06 } from '../../../../../styling/colours';
import { MapNode, MapNodeType } from '../mapNode';
import {
  JunctionTreeNode,
  NodeState,
  TreeNodeType,
  TreeNodeWithMapNodeEquivalent,
  TreeNodeWithSingleChild,
  TreeNodeWithStats,
} from '../treeNode';
import { getMapId, pipeMapNodeFromTreeNodeParent } from './mapHelpers';
import { countJunctionsInNode, getMaxDepth, ROOT_ID, TreeWithStats } from './treeHelpers';

export class MapFromTree {
  private readonly MAP_NODES_PER_TREE_NODE = 2;
  readonly map: Array<MapNode>;
  readonly edges: Array<Edge>;
  readonly tree: TreeWithStats;
  readonly mapDepth: number;
  readonly dynamicallyStyleEdges: boolean;

  constructor(tree: TreeWithStats, dynamicallyStyleEdges: boolean) {
    this.map = [];
    this.mapDepth = getMaxDepth(tree) * this.MAP_NODES_PER_TREE_NODE;
    this.tree = tree;
    this.edges = [];
    this.dynamicallyStyleEdges = dynamicallyStyleEdges;
    this.placeNextTreeNodeInMap(tree[ROOT_ID], 0, 0, '');
  }

  private placeNextTreeNodeInMap = (treeNode: TreeNodeWithStats, x: number, y: number, mapNodeParentId: string) => {
    switch (treeNode.nodeType) {
      // 0 children on node
      case TreeNodeType.Manifold:
        const mapNode = this.mapNodeFromTreeNode(treeNode, this.mapDepth, x, y);
        this.addMapNodeToMap(mapNode, mapNodeParentId);
        break;

      // 1 child on node
      case TreeNodeType.HeatSource:
      case TreeNodeType.Pump:
        this.addSingleChildTreeNodeAndChildrenToMap(treeNode, x, y, mapNodeParentId);
        break;

      // 2 children on node
      case TreeNodeType.Junction:
        const dropDepth = (treeNode.firstChild ? countJunctionsInNode(this.tree, treeNode.firstChild.id) : 0) + 1;
        this.addJunctionAndChildrenNodeToMap(treeNode, x, y, mapNodeParentId, dropDepth);
        break;
    }
  };

  private addMapNodeToMap = (mapNode: MapNode, mapNodeParentId: string) => {
    const state = mapNode.data.state;
    const style = this.dynamicallyStyleEdges
      ? this.getStyle(state, mapNode.type, mapNode.data.markedForDeletion)
      : { stroke: colourRed09, strokeWidth: 4 };

    this.map.push(mapNode);
    this.edges.push({
      id: mapNodeParentId + '-' + mapNode.id,
      source: mapNodeParentId,
      target: mapNode.id,
      type: 'step',
      animated:
        this.dynamicallyStyleEdges &&
        (mapNode.data.markedForDeletion ||
          (mapNode.type === MapNodeType.Pipe && (state === 'Warning' || state === 'Error'))),
      style,
    });
  };

  private getStyle(state: NodeState, mapNodeType: MapNodeType, markedForDeletion: boolean) {
    if (markedForDeletion) {
      return { stroke: colourPurple06 };
    }

    if (mapNodeType === MapNodeType.Pipe && state === 'Warning') {
      return { stroke: colourYellow06 };
    }

    if (mapNodeType === MapNodeType.Pipe && state === 'Error') {
      return { stroke: colourRed06 };
    }
  }

  private addSingleChildTreeNodeAndChildrenToMap = (
    treeNode: TreeNodeWithSingleChild,
    x: number,
    y: number,
    mapNodeParentId: string,
  ) => {
    const mapNode = this.mapNodeFromTreeNode(treeNode, this.mapDepth, x, y);
    this.addMapNodeToMap(mapNode, mapNodeParentId);

    const pipeNode = pipeMapNodeFromTreeNodeParent(treeNode, this.mapDepth, x + 1, y, true);
    this.addMapNodeToMap(pipeNode, mapNode.id);

    if (treeNode.firstChild.id) {
      this.placeNextTreeNodeInMap(this.tree[treeNode.firstChild.id], x + 2, y, pipeNode.id);
    }
  };

  private addJunctionAndChildrenNodeToMap = (
    treeNode: JunctionTreeNode,
    x: number,
    y: number,
    mapNodeParentId: string,
    dropDepth: number,
  ) => {
    const firstChildPipeMapNode = pipeMapNodeFromTreeNodeParent(treeNode, this.mapDepth, x + 1, y, true);
    this.addMapNodeToMap(firstChildPipeMapNode, mapNodeParentId);
    if (treeNode.firstChild.id) {
      this.placeNextTreeNodeInMap(this.tree[treeNode.firstChild.id], x + 2, y, firstChildPipeMapNode.id);
    }

    const secondChildPipeMapNode = pipeMapNodeFromTreeNodeParent(treeNode, this.mapDepth, x + 1, y + dropDepth, false);

    this.addMapNodeToMap(secondChildPipeMapNode, mapNodeParentId);
    if (treeNode.secondChild.id) {
      this.placeNextTreeNodeInMap(this.tree[treeNode.secondChild.id], x + 2, y + dropDepth, secondChildPipeMapNode.id);
    }
  };

  private mapNodeFromTreeNode = (node: TreeNodeWithMapNodeEquivalent, depth: number, x: number, y: number): MapNode => {
    const id = getMapId(depth, x, y);
    const nodeDetails = {
      id,
      position: { x, y },
    };

    const standardData = {
      treeNodeId: node.id,
      markedForDeletion: node.markedForDeletion,
    };

    switch (node.nodeType) {
      case TreeNodeType.HeatSource:
        return {
          ...nodeDetails,
          type: MapNodeType.HeatSource,
          data: { ...node.fieldData, ...node.stats, ...standardData },
        };

      case TreeNodeType.Pump:
        return {
          ...nodeDetails,
          type: MapNodeType.Pump,
          data: { ...node.fieldData, ...node.stats, ...standardData },
        };

      case TreeNodeType.Manifold:
        return {
          ...nodeDetails,
          type: MapNodeType.Manifold,
          data: { ...node.fieldData, ...node.stats, ...standardData },
        };
    }
  };
}
